/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import {
    IpGroupApiService,
    IpGroupModel,
    LbErrorService,
    NetworkingApiService,
    NetworkInterfaceModel
} from '../../../../shared';
import {
    LanguageService,
    LogService,
    ModalService,
    NavigationService,
    UtilsService,
    ValidationService
} from '../../../../../../core/services';
import { concatMap, takeUntil } from 'rxjs/operators';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { ipValidator, nameValidation } from '../../../../../../core/directives';

@Component({
    selector: 'app-new-fips-groups',
    templateUrl: './add-edit-ip-groups.component.html',
    styleUrls: ['./add-edit-ip-groups.component.scss']
})

export class AddEditIpGroupsComponent extends BaseComponent {

    /**
     * New fip group: FormGroup.
     */
    public formGroup: FormGroup;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        mask: [],
        interfaces: []
    };

    /**
     * Store the id of the fip group being edited.
     */
    public editId: string = null;

    /**
     * To store the interface's data.
     * @private
     */
    private interfaces: NetworkInterfaceModel[];

    /**
     * To store the group's edit data.
     * @private
     */
    private groupEdit: IpGroupModel;

    /**
     * To store the initial form data.
     * @private
     */
    private initialFormData: any = {};

    /**
     * To store the group's data.
     * @private
     */
    private groups: IpGroupModel[] = [];

    constructor(
        public modalService: ModalService,
        private formBuilder: FormBuilder,
        private ipGroupApiService: IpGroupApiService,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private logService: LogService,
        private navigationService: NavigationService,
        private networkingApiService: NetworkingApiService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();

        this.editId = this.route.snapshot.paramMap.get('id');

        this.setUpFormGroup();
        this.setUpFormSubscribers();
        this.setUpPageData(this.editId);
    }

    /**
     * Navigate to supplied URL.
     * @param url
     */
    public navigateToURL(url: string): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url)).then().catch();
    }

    /**
     * Ask user to confirm the changes if the group name has changed.
     */
    public submitGroupCheck(): Observable<any> {
        const group = this.formGroup.value;
        if (!group.id && group.defaultGateway) {
            this.confirmGatewayChange(group);
        } else {
            return new Observable((observer: Subscriber<any>): void => {
                this.submitGroup(group)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe({
                        next: (data: any): void => {
                            observer.next(data);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.logService.error('AddEditIpGroupsComponent: submitGroup', error.message);
                            observer.error(error);
                            observer.complete();
                        }
                    });
            });
        }
    }

    /**
     * Check form changes before update.
     */
    public updateGroupCheck(): Observable<any> {
        const group = this.formGroup.value;
        if (group.defaultGateway) {
            this.confirmGatewayChange(group);
        } else {
            return new Observable<any>((observer: Subscriber<any>) => {
                this.submitIpGroupHelper(group)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.logService.error('AddEditIpGroupsComponent:updateGroupCheck(): error', error.message);
                            observer.error(error.message);
                            observer.complete();
                        }
                    });
            });
        }
    }

    /**
     * Confirm gateway change.
     * @param group
     * @private
     */
    private confirmGatewayChange(group: any): void {
        const confirmationObject = {
            title: 'ip_groups_gateway_changed',
            message: this.formatGatewayChangeMessage(group),
            callBack: !group.id ? this.submitGroup.bind(this, group) : this.submitIpGroupHelper.bind(this, group),
            options: { translateText: false }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Format gateway change warning message.
     * @param group
     * @private
     */
    private formatGatewayChangeMessage(group: any): string {
        return `${this.languageService.translate('ip_groups_gateway_changed_text')} '${group.name}'.
            ${this.languageService.translate('ip_groups_disconnection_warning')}`;
    }

    /**
     * Submit ip group helper.
     * @param group
     * @private
     */
    private submitIpGroupHelper(group: any): Observable<any> {
        return new Observable((observer: Subscriber<any>) => {
            if (group.id && this.groupEdit?.name !== group.name) {
                this.confirmGroupNameChange(group);
            } else {
                this.submitGroup(group)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            this.logService.error('AddEditIpGroupsComponent:submitIpGroupHelper(): error', error.message);
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        });
    }

    /**
     * Confirm group name changed.
     * @param group
     * @private
     */
    private confirmGroupNameChange(group: any): void {
        const confirmationObject = {
            title: 'ip_name_change',
            message: 'ip_name_change_text',
            callBack: this.submitGroup.bind(this, group),
            options: { translateText: true }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Submit the group form values.
     * @param group
     * @private
     */
    private submitGroup(group: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.getApiCall(group)
                .pipe(concatMap(group => this.assignGroupInterface(group)))
                .subscribe({
                    next: (): void => {
                        this.navigateToURL('/network/ip-groups');
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.formGroup, error.error.errors, error.uId);
                        this.logService.error('AddEditIpGroupsComponent:submitGroup', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get the API call.
     * @param group
     * @private
     */
    private getApiCall(group: any): Observable<any> {
        return group.id
            ? this.ipGroupApiService.editGroup(group)
            : this.ipGroupApiService.addGroup(group);
    }

    /**
     * Assign interface to group.
     * @param group
     * @private
     */
    private assignGroupInterface(group: IpGroupModel): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const editId: string = (group ? group.id : this.groupEdit.id);
            this.networkingApiService.assignGroupInterface(this.formGroup.get('interfaces').value, editId)
                .subscribe({
                    next: (): void => {
                        this.groupEdit = null;
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.formGroup, error.error.errors);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Initialise the form.
     * @private
     */
    private setUpFormGroup(): void {
        this.formGroup = this.formBuilder.group({
            id: [null],
            name: [null, [Validators.required, nameValidation]],
            networkIp: [null, [Validators.required, ipValidator]],
            networkMask: [{ value: null, disabled: true }, Validators.required],
            interfaces: [null, Validators.required],
            gateway: [null, ipValidator],
            defaultGateway: [{ value: null, disabled: true }]
        });

        this.initialFormData = this.formGroup.value;
    }

    /**
     * Watch the 'enable' IPv4 and IPv6 checkboxes and set disabled on relevant the inputs.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.validateFormAgainstInitialData();
        this.checkForDuplicateName();
        this.setUpMaskDropdownBaseOnIP();
        this.checkForGateway();
    }

    /**
     * Validate the current form data again initial to see if the button should be enabled or disabled.
     * @private
     */
    private validateFormAgainstInitialData(): void {
        this.formGroup.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('form', (): void => {
                        if (!this.utilsService.hasFormDataChanged(this.initialFormData, form)) {
                            this.formGroup.markAsPristine();
                        } else {
                            this.formGroup.markAsDirty();
                        }
                    });
                }
            });
    }

    /**
     * Check the group name to ensure there are no duplicates.
     * @private
     */
    private checkForDuplicateName(): void {
        this.formGroup.get('name').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: name => {
                    this.utilsService.debounce('checkForDuplicateName', () => {
                        const duplicateGroupName = this.groups.find(
                            (row: IpGroupModel) => row.name.toLowerCase() === (name?.toLowerCase()) && !this.groupEdit
                        );
                        const formGroupName: AbstractControl = this.formGroup.get('name');
                        if (duplicateGroupName) {
                            formGroupName.setErrors({ 'duplicateGroupName': `${this.languageService.translate('duplicate_group_name')}` });
                            formGroupName.markAsTouched();
                        } else if (formGroupName.hasError('duplicateGroupName')) {
                            formGroupName.setErrors({ 'duplicateGroupName': null });
                            formGroupName.updateValueAndValidity();
                            formGroupName.markAsUntouched();
                        }
                    }, 350);
                }
            });
    }

    /**
     * Set up mask dropdown base on IP.
     * @private
     */
    private setUpMaskDropdownBaseOnIP(): void {
        this.formGroup.get('networkIp').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (value: string): void => {
                    this.utilsService.debounce('networkIp', (): void => {
                        if (ValidationService.ipV4Check(value)) {
                            this.dropdown.mask = this.utilsService.createRangeArray(30, 8);
                        } else if (ValidationService.ipV6Check(value)) {
                            this.dropdown.mask = this.utilsService.createRangeArray(126, 96);
                        }

                        this.formGroup.get('networkMask').enable();
                    });
                }
            });
    }

    /**
     * Check for gateway.
     * @private
     */
    private checkForGateway(): void {
        this.formGroup.get('gateway').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (value: string): void => {
                    this.utilsService.debounce('gateway', (): void => {
                        if (value.length) {
                            this.formGroup.get('defaultGateway').enable();
                        } else {
                            this.formGroup.get('defaultGateway').disable();
                        }
                    });
                }
            });
    }

    /**
     * Set up the page data and add/edit values.
     * @param editId
     * @private
     */
    private setUpPageData(editId: string): void {
        forkJoin({
            groups: this.ipGroupApiService.getGroups(),
            interfaces: this.networkingApiService.getInterfaces()
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    if (dataGroup['interfaces']?.length && dataGroup['groups']?.length) {
                        this.groups = dataGroup['groups'];
                        this.interfaces = dataGroup['interfaces'];
                        this.dropdown.interfaces = this.setUpInterfacesDropdown(this.interfaces);
                        if (editId) {
                            const group: IpGroupModel = this.groups.find((item: IpGroupModel): boolean => item.id === editId);
                            this.groupEdit = group;
                            this.patchEditFipGroupForm(group);
                        }
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AddEditIpGroupsComponent: setUpPageData', error.message);
                }
            });
    }

    /**
     * Set up network interfaces dropdown
     * @param interfaces
     * @private
     */
    private setUpInterfacesDropdown(interfaces: NetworkInterfaceModel[]): DropdownModel[] {
        const interfacesArray = [];
        for (const iface of interfaces) {
            if (iface.type !== 'bondChild') {
                interfacesArray.push(<DropdownModel>{
                    key: iface.id,
                    value: `${iface.name} (${iface.settings.interfaceName})`
                });
            }
        }

        return interfacesArray;
    }

    /**
     * Patch the form with the values of the FIP group to edit.
     * @private
     */
    private patchEditFipGroupForm(group: IpGroupModel): void {
        group['interfaces'] = group.assignedInterfaces[0].interfaceId;
        this.formGroup.patchValue(group);
        this.initialFormData = this.formGroup.value;
    }
}
